import { arcade, bindPointer } from './arcade.js';
"use strict";
    (function () {
        // ============================================================
        // Lode Runner - authentic rebuild
        // Pure vanilla JS + Canvas 2D, self-contained, no dependencies.
        // ============================================================

        const canvas = document.getElementById('canvas');
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = false;

        // Respect users who prefer reduced motion (calms flashing exit ladder, banners, etc.)
        const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        let prefersReducedMotion = reducedMotionQuery.matches;
        if (reducedMotionQuery.addEventListener) {
            reducedMotionQuery.addEventListener('change', (e) => { prefersReducedMotion = e.matches; });
        }

        // -------------------- Grid / timing constants --------------------
        const TILE = 24;                 // pixels per tile
        const COLS = 28;                 // grid width
        const ROWS = 21;                 // grid height (672x504 canvas)
        const RUN_SPEED = 1.6;           // player horizontal/ladder px per frame
        const CLIMB_SPEED = 1.5;         // player ladder px per frame
        const FALL_SPEED = 2.0;          // CONSTANT fall speed (no gravity accel)
        const GUARD_SPEED = 1.15;        // guards a touch slower than player
        const GUARD_FALL_SPEED = 2.0;
        const DIG_REFILL_MS = 5000;      // hole stays open this long, then refills
        const DIG_CRUMBLE_MS = 260;      // crumble animation duration
        const DIG_REFILL_WARN_MS = 1200; // hole "flashes" before refilling
        const GUARD_TRAP_CLIMB_MS = 1400;// how long a guard stays stuck before climbing out
        const GUARD_RESPAWN_MS = 1600;   // delay before a killed guard returns
        const BANNER_MS = 1500;          // "LEVEL n" banner duration

        // Tile codes (stored in the grid)
        const T = {
            EMPTY: 0,
            BRICK: 1,   // diggable
            SOLID: 2,   // concrete - NOT diggable
            LADDER: 3,
            BAR: 4,     // rope / bar (traverse along top of cell)
            GOLD: 5,
            EXIT: 6     // hidden exit ladder (revealed when all gold collected)
        };

        // -------------------- Level maps (ASCII) --------------------
        // Legend:
        //   '2' solid concrete   '1' brick (diggable)   '#' brick (alias)
        //   '=' ladder           '-' bar/rope           '$' gold
        //   'X' exit ladder cell (hidden until gold done)
        //   '@' player start     'E' guard start        ' ' empty
        // Each row is COLS (28) characters; each map is ROWS (21) rows.
        const LEVELS = [
            // ---------- Level 1: gentle intro (5 floors, ladders link all) ----------
            [
                "2222222222222222222222222222",
                "2            X             2",
                "2     =      =       =     2",
                "2  $  =  $   =   $   =  $  2",
                "2#####=######=#######=#####2",
                "2     =              =     2",
                "2     =              =     2",
                "2  $  =       $   E  =  $  2",
                "2#####=####=####=####=#####2",
                "2          =    =          2",
                "2          =    =          2",
                "2   $   E  = $  =     $    2",
                "2#####=####=####=####=#####2",
                "2     =              =     2",
                "2     =              =     2",
                "2   $ =  @        $  =E $  2",
                "2#####=####=####=####=#####2",
                "2          =    =          2",
                "2       $  =    =  $       2",
                "2##########=####=##########2",
                "2222222222222222222222222222"
            ],
            // ---------- Level 2: ropes + 4 guards ----------
            [
                "2222222222222222222222222222",
                "2    X                     2",
                "2    =                =    2",
                "2  $ =       $      $ =  $ 2",
                "2####=################=####2",
                "2    =                =    2",
                "2    =  ---=----=---  =    2",
                "2  $ =   $ =    =  E$ =  $ 2",
                "2####=#####=####=#####=####2",
                "2    =     =    =     =    2",
                "2    =     =    =     =    2",
                "2  $ =     =E$  =     =  $ 2",
                "2####=###=#=####=#=###=####2",
                "2    =   =        =   =    2",
                "2    =  -=--------=-  =    2",
                "2  $ =  E=   $    = E =  $ 2",
                "2####=###=###=####=###=####2",
                "2    =       =        =    2",
                "2    =  $    @      $ =    2",
                "2####=#######=########=####2",
                "2222222222222222222222222222"
            ],
            // ---------- Level 3: concrete accents + 6 guards ----------
            [
                "2222222222222222222222222222",
                "2   =        X         =   2",
                "2  $=        =         =$  2",
                "2###=########=#########=###2",
                "2   =        =         =   2",
                "2  $=      $ = E$      =$  2",
                "2###=####=###=####=####=###2",
                "2   =    =        =    =   2",
                "2   = $  =E$   E$ =  $ =   2",
                "2###=##2#=###=####=#2##=###2",
                "2   =        =         =   2",
                "2  $=    E$  =   $     =$  2",
                "2###=####=###=####=####=###2",
                "2   =    =        =    =   2",
                "2   = $  =  E$    =  $ =   2",
                "2###=##2#=###=####=#2##=###2",
                "2   =        =         =   2",
                "2  $=    $ @ =   E$    =$  2",
                "2###=########=#########=###2",
                "2                          2",
                "2222222222222222222222222222"
            ]
        ];

        // -------------------- Audio (chiptune via Web Audio) --------------------
        const Sound = (function () {
            let actx = null;
            let enabled = true;
            function ensure() {
                if (actx) return actx;
                try {
                    const AC = window.AudioContext || window.webkitAudioContext;
                    if (!AC) { enabled = false; return null; }
                    actx = new AC();
                } catch (e) { enabled = false; actx = null; }
                return actx;
            }
            function resume() {
                const a = ensure();
                if (a && a.state === 'suspended') { a.resume().catch(() => {}); }
            }
            function blip(freq, dur, type, vol, slideTo) {
                if (!enabled || !arcade.soundEnabled) return;
                const a = ensure();
                if (!a) return;
                try {
                    const o = a.createOscillator();
                    const g = a.createGain();
                    o.type = type || 'square';
                    o.frequency.setValueAtTime(freq, a.currentTime);
                    if (slideTo) {
                        o.frequency.exponentialRampToValueAtTime(
                            Math.max(40, slideTo), a.currentTime + dur);
                    }
                    g.gain.setValueAtTime(0.0001, a.currentTime);
                    g.gain.exponentialRampToValueAtTime(vol || 0.12, a.currentTime + 0.01);
                    g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + dur);
                    o.connect(g); g.connect(a.destination);
                    o.start();
                    o.stop(a.currentTime + dur + 0.02);
                } catch (e) { /* ignore */ }
            }
            function seq(notes) {
                if (!enabled || !arcade.soundEnabled) return;
                const a = ensure();
                if (!a) return;
                let t = 0;
                notes.forEach(n => {
                    setTimeout(() => blip(n[0], n[1], n[2] || 'square', n[3] || 0.12, n[4]), t);
                    t += n[5] || (n[1] * 1000);
                });
            }
            return {
                resume,
                dig:        () => blip(180, 0.18, 'square', 0.10, 90),
                gold:       () => seq([[660, 0.07, 'square', 0.12, 0, 70], [990, 0.12, 'square', 0.12]]),
                trap:       () => blip(140, 0.22, 'sawtooth', 0.12, 70),
                guardDie:   () => blip(120, 0.32, 'sawtooth', 0.13, 50),
                death:      () => seq([[400, 0.12, 'sawtooth', 0.14, 80, 110],
                                       [300, 0.12, 'sawtooth', 0.14, 60, 110],
                                       [180, 0.30, 'sawtooth', 0.14, 50]]),
                step:       () => blip(220, 0.03, 'square', 0.04),
                climb:      () => blip(300, 0.03, 'triangle', 0.04),
                levelDone:  () => seq([[523, 0.10], [659, 0.10], [784, 0.10], [1047, 0.22]]),
                gameOver:   () => seq([[523, 0.16, 'square', 0.13],
                                       [415, 0.16, 'square', 0.13],
                                       [330, 0.16, 'square', 0.13],
                                       [262, 0.40, 'square', 0.13]])
            };
        })();

        // -------------------- Pixel-art sprite engine --------------------
        // Sprites are 12x12 pixel grids; each cell is a palette index ('.' = transparent).
        // drawSprite renders them as integer-aligned rects scaled to TILE size.
        const SPRITE_PX = 12;
        const PX = TILE / SPRITE_PX; // 2px per sprite-pixel at 24px tiles

        // Palette characters used across sprites
        const PAL = {
            '.': null,
            'W': '#f4f4f4', // white
            'r': '#ff3030', // red
            'k': '#101018', // near black
            'y': '#ffd700', // gold
            'o': '#ffae00', // gold shadow
            'm': '#ff35d0', // magenta (guard)
            'p': '#b01090', // magenta shadow
            's': '#101820'  // dark eye
        };

        function S(rows) { return rows; } // identity, for readability

        // Runner frames (white body, red trim). 12x12.
        const RUNNER = {
            standR: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkWWkW...",
                "...WkkkkW...",
                "....WWWW....",
                "..rrWWWWrr..",
                "..rrWWWWrr..",
                "....WWWW....",
                "....W..W....",
                "...WW..WW...",
                "..WW....WW..",
                ".WW......WW."
            ]),
            run1R: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkWWkW...",
                "...WkkkkW...",
                "....WWWW....",
                "..rrWWWWrr..",
                "rr.rWWWWr...",
                "....WWWW....",
                "...WW..W....",
                "..WW...WW...",
                ".WW.....W...",
                "WW.......WW."
            ]),
            run2R: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkWWkW...",
                "...WkkkkW...",
                "....WWWW....",
                "..rrWWWWrr..",
                "...rWWWWr.rr",
                "....WWWW....",
                "....W..WW...",
                "...WW...WW..",
                "...W.....WW.",
                ".WW.......WW"
            ]),
            climb1: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkkkkW...",
                "...WkkkkW...",
                "..W.WWWW.W..",
                ".WW.WWWW.WW.",
                "W...WWWW...W",
                "....WWWW....",
                "...WW..WW...",
                "...W....W...",
                "..WW....WW..",
                "..W......W.."
            ]),
            climb2: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkkkkW...",
                "...WkkkkW...",
                "W...WWWW...W",
                ".WW.WWWW.WW.",
                "..W.WWWW.W..",
                "....WWWW....",
                "...W....W...",
                "...WW..WW...",
                "..W......W..",
                "..WW....WW.."
            ]),
            hangR: S([
                "WW........WW",
                ".WW......WW.",
                "..WWWWWWWW..",
                "...WkkkkW...",
                "...WkWWkW...",
                "...WkkkkW...",
                "..rrWWWWrr..",
                "..rrWWWWrr..",
                "....WWWW....",
                "....W..W....",
                "...WW..WW...",
                "..WW....WW.."
            ]),
            fallR: S([
                "....WWWW....",
                "...WkkkkW...",
                "...WkWWkW...",
                "...WkkkkW...",
                "rr..WWWW..rr",
                ".rr.WWWW.rr.",
                "..rrWWWWrr..",
                "....WWWW....",
                "...WW..WW...",
                "..WW....WW..",
                "..W......W..",
                ".WW......WW."
            ])
        };

        // Guard frames (magenta body). 12x12.
        const GUARD = {
            standR: S([
                "....mmmm....",
                "...mssssm...",
                "...msWWsm...",
                "...msssm....",
                "....mmmm....",
                "..ppmmmmpp..",
                "..ppmmmmpp..",
                "....mmmm....",
                "....m..m....",
                "...mm..mm...",
                "..mm....mm..",
                ".mm......mm."
            ]),
            run1R: S([
                "....mmmm....",
                "...mssssm...",
                "...msWWsm...",
                "...msssm....",
                "....mmmm....",
                "..ppmmmmpp..",
                "pp.pmmmmp...",
                "....mmmm....",
                "...mm..m....",
                "..mm...mm...",
                ".mm.....m...",
                "mm.......mm."
            ]),
            run2R: S([
                "....mmmm....",
                "...mssssm...",
                "...msWWsm...",
                "...msssm....",
                "....mmmm....",
                "..ppmmmmpp..",
                "...pmmmmp.pp",
                "....mmmm....",
                "....m..mm...",
                "...mm...mm..",
                "...m.....mm.",
                ".mm.......mm"
            ]),
            climb1: S([
                "....mmmm....",
                "...mssssm...",
                "...msssm....",
                "...mssssm...",
                "..m.mmmm.m..",
                ".mm.mmmm.mm.",
                "m...mmmm...m",
                "....mmmm....",
                "...mm..mm...",
                "...m....m...",
                "..mm....mm..",
                "..m......m.."
            ]),
            climb2: S([
                "....mmmm....",
                "...mssssm...",
                "...msssm....",
                "...mssssm...",
                "m...mmmm...m",
                ".mm.mmmm.mm.",
                "..m.mmmm.m..",
                "....mmmm....",
                "...m....m...",
                "...mm..mm...",
                "..m......m..",
                "..mm....mm.."
            ]),
            fallR: S([
                "....mmmm....",
                "...mssssm...",
                "...msWWsm...",
                "...msssm....",
                "pp..mmmm..pp",
                ".pp.mmmm.pp.",
                "..ppmmmmpp..",
                "....mmmm....",
                "...mm..mm...",
                "..mm....mm..",
                "..m......m..",
                ".mm......mm."
            ])
        };

        // Pre-bake sprites + their mirrored variants into per-pixel rect lists for speed.
        function bakeSprite(rows, palette) {
            const list = [];
            for (let y = 0; y < rows.length; y++) {
                const line = rows[y];
                for (let x = 0; x < line.length; x++) {
                    const c = palette[line[x]];
                    if (c) list.push([x, y, c]);
                }
            }
            return list;
        }
        function mirrorRows(rows) {
            return rows.map(line => line.split('').reverse().join(''));
        }
        const SPR = {};
        function register(name, rows) {
            SPR[name + 'R'] = bakeSprite(rows, PAL);
            SPR[name + 'L'] = bakeSprite(mirrorRows(rows), PAL);
        }
        // Runner
        register('rStand', RUNNER.standR);
        register('rRun1', RUNNER.run1R);
        register('rRun2', RUNNER.run2R);
        register('rFall', RUNNER.fallR);
        register('rHang', RUNNER.hangR);
        SPR['rClimb1'] = bakeSprite(RUNNER.climb1, PAL);
        SPR['rClimb2'] = bakeSprite(RUNNER.climb2, PAL);
        // Guard
        register('gStand', GUARD.standR);
        register('gRun1', GUARD.run1R);
        register('gRun2', GUARD.run2R);
        register('gFall', GUARD.fallR);
        SPR['gClimb1'] = bakeSprite(GUARD.climb1, PAL);
        SPR['gClimb2'] = bakeSprite(GUARD.climb2, PAL);

        function drawSprite(name, px, py) {
            const list = SPR[name];
            if (!list) return;
            const ox = Math.round(px);
            const oy = Math.round(py);
            for (let i = 0; i < list.length; i++) {
                const p = list[i];
                ctx.fillStyle = p[2];
                ctx.fillRect(ox + p[0] * PX, oy + p[1] * PX, PX, PX);
            }
        }

        // -------------------- Level model --------------------
        class Level {
            constructor(map, levelNumber) {
                this.levelNumber = levelNumber;
                this.grid = [];        // current tile state
                this.base = [];        // pristine (used so refilled bricks restore exactly)
                this.holes = [];       // active dug holes
                this.goldTotal = 0;    // fixed: total gold pieces in the level (never changes)
                this.goldCollected = 0;// gold the PLAYER has banked
                this.playerStart = { col: 1, row: 1 };
                this.guardStarts = [];
                this.exitCol = -1;
                this.exitRow = -1;
                this.exitRevealed = false;
                this.parse(map);
            }

            parse(map) {
                for (let r = 0; r < ROWS; r++) {
                    this.grid[r] = [];
                    this.base[r] = [];
                    const line = map[r] || '';
                    for (let c = 0; c < COLS; c++) {
                        const ch = line[c] || ' ';
                        let tile = T.EMPTY;
                        switch (ch) {
                            case '2': tile = T.SOLID; break;
                            case '1':
                            case '#': tile = T.BRICK; break;
                            case '=': tile = T.LADDER; break;
                            case '-': tile = T.BAR; break;
                            case '$':
                                tile = T.GOLD; this.goldTotal++; break;
                            case 'X':
                                this.exitCol = c; this.exitRow = r; tile = T.EMPTY; break;
                            case '@':
                                this.playerStart = { col: c, row: r }; tile = T.EMPTY; break;
                            case 'E':
                                this.guardStarts.push({ col: c, row: r }); tile = T.EMPTY; break;
                            default: tile = T.EMPTY;
                        }
                        this.grid[r][c] = tile;
                        // base stores structural tiles only (gold/exit are not structure)
                        this.base[r][c] = (tile === T.GOLD || tile === T.EXIT) ? T.EMPTY : tile;
                    }
                }
            }

            get(c, r) {
                if (r < 0 || r >= ROWS || c < 0 || c >= COLS) return T.SOLID;
                return this.grid[r][c];
            }
            set(c, r, t) {
                if (r < 0 || r >= ROWS || c < 0 || c >= COLS) return;
                this.grid[r][c] = t;
            }
            isLadder(c, r) { return this.get(c, r) === T.LADDER || this.get(c, r) === T.EXIT; }
            isBar(c, r) { return this.get(c, r) === T.BAR; }
            // Can an actor stand/rest centered on this cell?
            isBlocked(c, r) {
                const t = this.get(c, r);
                return t === T.BRICK || t === T.SOLID;
            }

            collectGold(c, r) {
                if (this.get(c, r) === T.GOLD) {
                    this.set(c, r, T.EMPTY);
                    this.goldCollected++;
                    return true;
                }
                return false;
            }

            // How many gold pieces still need to be banked by the player.
            goldRemaining() { return this.goldTotal - this.goldCollected; }

            // Place a dropped gold piece on the nearest valid empty, supported cell so it is
            // always re-collectible. goldTotal is fixed, so no counter bookkeeping is needed.
            dropGold(c, r) {
                const tryPlace = (cc, rr) => {
                    if (rr < 1 || rr >= ROWS - 1 || cc < 1 || cc >= COLS - 1) return false;
                    if (this.grid[rr][cc] !== T.EMPTY) return false;
                    // Never drop into an open dug hole: it refills to brick and
                    // would destroy the gold, making the level uncompletable.
                    if (this.isOpenHole(cc, rr)) return false;
                    // need solid/ladder support below so the piece isn't floating in a pit
                    const below = this.get(cc, rr + 1);
                    const supported = below === T.BRICK || below === T.SOLID ||
                                      below === T.LADDER;
                    // also accept resting on a ladder/bar tile itself
                    const onClimb = this.isLadder(cc, rr) || this.isBar(cc, rr);
                    if (supported || onClimb) {
                        this.grid[rr][cc] = T.GOLD;
                        return true;
                    }
                    return false;
                };
                // search outward (rings) from the drop point
                if (tryPlace(c, r)) return;
                for (let radius = 1; radius < Math.max(COLS, ROWS); radius++) {
                    for (let dr = -radius; dr <= radius; dr++) {
                        for (let dc = -radius; dc <= radius; dc++) {
                            if (Math.abs(dr) !== radius && Math.abs(dc) !== radius) continue;
                            if (tryPlace(c + dc, r + dr)) return;
                        }
                    }
                }
                // Last resort: scan the whole board for any valid floor cell.
                for (let rr = ROWS - 2; rr >= 1; rr--) {
                    for (let cc = 1; cc < COLS - 1; cc++) {
                        if (tryPlace(cc, rr)) return;
                    }
                }
            }

            revealExitIfDone() {
                if (!this.exitRevealed && this.goldCollected >= this.goldTotal &&
                    this.exitCol >= 0) {
                    this.exitRevealed = true;
                    // Build the escape ladder from the exit marker upward to the top row.
                    for (let r = this.exitRow; r >= 1; r--) {
                        if (this.get(this.exitCol, r) === T.EMPTY) {
                            this.set(this.exitCol, r, T.EXIT);
                        }
                    }
                    return true;
                }
                return false;
            }

            // Dig a hole at (c,r) if it's brick and the cell above is clear to drop into.
            dig(c, r) {
                if (this.get(c, r) !== T.BRICK) return false;
                if (this.isBlocked(c, r - 1)) return false; // need clearance above the hole
                this.set(c, r, T.EMPTY);
                this.holes.push({
                    c, r,
                    state: 'crumble',
                    t: 0,
                    open: 0
                });
                return true;
            }

            holeAt(c, r) {
                for (let i = 0; i < this.holes.length; i++) {
                    if (this.holes[i].c === c && this.holes[i].r === r) return this.holes[i];
                }
                return null;
            }

            // An OPEN dug hole has an invisible floor at its base: actors fall INTO it and
            // rest there (this is what traps guards), until the hole refills.
            isOpenHole(c, r) {
                const h = this.holeAt(c, r);
                return !!h && h.state === 'open';
            }

            update(dt, game) {
                for (let i = this.holes.length - 1; i >= 0; i--) {
                    const h = this.holes[i];
                    h.t += dt;
                    if (h.state === 'crumble') {
                        if (h.t >= DIG_CRUMBLE_MS) { h.state = 'open'; h.t = 0; }
                    } else if (h.state === 'open') {
                        h.open += dt;
                        if (h.open >= DIG_REFILL_MS) {
                            // refill: kill anything caught inside
                            game.onHoleRefill(h);
                            this.set(h.c, h.r, this.base[h.r][h.c] === T.EMPTY ? T.BRICK : this.base[h.r][h.c]);
                            this.holes.splice(i, 1);
                        }
                    }
                }
            }

            holeRefilling(h) {
                return h.state === 'open' && (DIG_REFILL_MS - h.open) <= DIG_REFILL_WARN_MS;
            }
        }

        // -------------------- Actor base (lane-snapped movement) --------------------
        // Position is the actor's top-left in pixels but logic keeps it lane-aligned.
        const ALIGN = 3; // px tolerance to consider "on a lane line"

        function nearCol(x) { return Math.round(x / TILE); }
        function nearRow(y) { return Math.round(y / TILE); }
        function colOf(x) { return Math.floor((x + TILE / 2) / TILE); }
        function rowOf(y) { return Math.floor((y + TILE / 2) / TILE); }

        class Actor {
            constructor(level, col, row) {
                this.level = level;
                this.x = col * TILE;
                this.y = row * TILE;
                this.dir = 1;           // facing
                this.frame = 0;         // animation accumulator
                this.pose = 'stand';    // stand|run|climb|hang|fall
                this.falling = false;
            }
            get col() { return colOf(this.x); }
            get row() { return rowOf(this.y); }

            // is this actor currently aligned to a column line?
            alignedX() { return Math.abs(this.x - nearCol(this.x) * TILE) <= ALIGN; }
            alignedY() { return Math.abs(this.y - nearRow(this.y) * TILE) <= ALIGN; }
            snapX() { this.x = nearCol(this.x) * TILE; }
            snapY() { this.y = nearRow(this.y) * TILE; }

            // Support test: is there something holding us up at this cell?
            footing(level) {
                const c = this.col, r = this.row;
                if (level.isLadder(c, r)) return 'ladder';        // standing on ladder cell
                if (level.isBar(c, r)) return 'bar';              // hanging on bar
                const below = level.get(c, r + 1);
                if (below === T.BRICK || below === T.SOLID || below === T.LADDER) return 'solid';
                // a trapped guard's head acts as floor (handled by game via occupancy)
                return null;
            }
        }

        // -------------------- Player --------------------
        class Player extends Actor {
            constructor(level, col, row) {
                super(level, col, row);
                this.dir = 1;
                this.dead = false;
            }

            update(dt, keys, game) {
                if (this.dead) return;
                const level = this.level;
                const c = this.col, r = this.row;
                let moved = false;

                const onLadder = level.isLadder(c, r);
                const onBar = level.isBar(c, r);
                const below = level.get(c, r + 1);
                const supportBelow = below === T.BRICK || below === T.SOLID || below === T.LADDER;
                const guardHeadBelow = game.guardActsAsFloor(c, r + 1);
                const inOpenHole = level.isOpenHole(c, r); // dug pit holds you at its base
                const ladderBelow = level.isLadder(c, r + 1);

                // ---- Determine if falling ----
                let falling = false;
                if (!onLadder && !onBar && !inOpenHole) {
                    if (!supportBelow && !guardHeadBelow) falling = true;
                }
                // On a bar, pressing down drops off it
                if (onBar && keys.down && this.alignedX()) falling = true;

                this.falling = falling;

                if (falling) {
                    // Constant fall, locked to column
                    this.snapX();
                    this.y += FALL_SPEED;
                    this.pose = 'fall';
                    const nr = this.row;
                    // land when the cell below the new row is support, OR we just entered an
                    // open hole (rest at its base and snap).
                    const landed = this.level.isBlocked(c, nr + 1) || level.isLadder(c, nr + 1) ||
                                   game.guardActsAsFloor(c, nr + 1) || level.isOpenHole(c, nr);
                    if (landed && this.alignedY()) this.snapY();
                    if (this.level.isBlocked(c, this.row + 1) && this.y > this.row * TILE) {
                        this.snapY();
                    }
                    game.afterPlayerMove();
                    return;
                }

                // ---- Vertical (ladder) movement ----
                if (keys.up && onLadder && this.alignedX()) {
                    this.snapX();
                    // don't climb into solid above
                    if (!level.isBlocked(c, r - 1)) {
                        this.y -= CLIMB_SPEED;
                        this.pose = 'climb';
                        moved = true;
                        if (game.stepTick()) Sound.climb();
                    }
                } else if (keys.down && this.alignedX() && (onLadder || ladderBelow)) {
                    this.snapX();
                    if (!level.isBlocked(c, r + 1)) {
                        this.y += CLIMB_SPEED;
                        this.pose = 'climb';
                        moved = true;
                        if (game.stepTick()) Sound.climb();
                    }
                }

                // ---- Horizontal movement ----
                // Only allowed when aligned to a row line (lane snap) unless on bar.
                if (keys.left || keys.right) {
                    const wantDir = keys.left ? -1 : 1;
                    this.dir = wantDir;
                    // must be vertically aligned to a lane to walk
                    if (this.alignedY()) {
                        this.snapY();
                        const nextC = wantDir < 0 ? colOf(this.x - 1) : colOf(this.x + TILE);
                        const targetCol = this.col + wantDir;
                        const blockedSide = level.isBlocked(targetCol, this.row);
                        if (!blockedSide) {
                            this.x += wantDir * RUN_SPEED;
                            this.pose = onBar ? 'hang' : 'run';
                            moved = true;
                            if (!onBar && game.stepTick()) Sound.step();
                        } else {
                            this.snapX();
                        }
                    }
                }

                if (!moved && !falling) {
                    this.pose = onBar ? 'hang' : (onLadder ? 'climb' : 'stand');
                    if (this.pose === 'climb' && !keys.up && !keys.down) this.pose = 'climb';
                    if (!onLadder && !onBar) this.pose = 'stand';
                }

                // keep in bounds
                this.x = Math.max(TILE, Math.min(this.x, (COLS - 2) * TILE));
                this.y = Math.max(0, Math.min(this.y, (ROWS - 1) * TILE));

                if (moved) this.frame += dt;
                game.afterPlayerMove();
            }

            draw() {
                const x = this.x, y = this.y;
                const d = this.dir < 0 ? 'L' : 'R';
                let name;
                if (this.pose === 'fall') name = 'rFall' + d;
                else if (this.pose === 'hang') name = 'rHang' + d;
                else if (this.pose === 'climb') {
                    name = (Math.floor(this.frame / 110) % 2) ? 'rClimb2' : 'rClimb1';
                } else if (this.pose === 'run') {
                    const f = Math.floor(this.frame / 90) % 3;
                    name = (f === 0 ? 'rStand' : (f === 1 ? 'rRun1' : 'rRun2')) + d;
                } else {
                    name = 'rStand' + d;
                }
                drawSprite(name, x, y);
            }
        }

        // -------------------- Guard --------------------
        class Guard extends Actor {
            constructor(level, col, row, id) {
                super(level, col, row);
                this.id = id;
                this.startCol = col;
                this.startRow = row;
                this.hasGold = false;
                this.trapped = false;
                this.trapTimer = 0;
                this.dead = false;
                this.respawnTimer = 0;
                this.reborn(col, row);
            }

            reborn(col, row) {
                this.x = col * TILE;
                this.y = row * TILE;
                this.dir = 1;
                this.trapped = false;
                this.trapTimer = 0;
                this.dead = false;
                this.pose = 'stand';
                this.falling = false;
                this.moveAcc = 0;
            }

            update(dt, game) {
                const level = this.level;

                if (this.dead) {
                    this.respawnTimer -= dt;
                    if (this.respawnTimer <= 0) {
                        // respawn at a top-area free cell
                        const spot = game.findRespawnSpot();
                        this.reborn(spot.col, spot.row);
                    }
                    return;
                }

                const c = this.col, r = this.row;

                // ---- Check trap: fallen into an open dug hole ----
                // The hole's base holds the guard (authentic); the surrounding brick walls
                // keep it from simply walking out until it can struggle free.
                const hole = level.holeAt(c, r);
                if (hole && hole.state === 'open' && this.alignedX() && this.alignedY()) {
                    if (!this.trapped) {
                        this.trapped = true;
                        this.trapTimer = 0;
                        this.snapX(); this.snapY();
                        if (this.hasGold) {
                            this.hasGold = false;
                            level.dropGold(c, r - 1 >= 1 ? r - 1 : r);
                        }
                        game.onGuardTrapped(this);
                    }
                }

                if (this.trapped) {
                    this.snapX(); this.snapY();
                    this.trapTimer += dt;
                    this.pose = 'stand';
                    // Climb out after a delay if the hole is still open (not refilled).
                    if (this.trapTimer >= GUARD_TRAP_CLIMB_MS) {
                        const stillHole = level.holeAt(c, r);
                        if (stillHole && stillHole.state === 'open') {
                            // hop out toward the side that is clear
                            const leftClear = !level.isBlocked(c - 1, r) && !level.isBlocked(c - 1, r - 1);
                            const rightClear = !level.isBlocked(c + 1, r) && !level.isBlocked(c + 1, r - 1);
                            const toward = (game.player.x < this.x) ? -1 : 1;
                            let outDir = 0;
                            if (toward < 0 && leftClear) outDir = -1;
                            else if (toward > 0 && rightClear) outDir = 1;
                            else if (leftClear) outDir = -1;
                            else if (rightClear) outDir = 1;
                            if (outDir !== 0) {
                                this.y = (r - 1) * TILE;
                                this.x = (c + outDir) * TILE;
                                this.trapped = false;
                                this.trapTimer = 0;
                            }
                        }
                    }
                    return;
                }

                // ---- Pick up gold ----
                // Guard lifts a gold piece off the board. goldTotal stays fixed; the piece is
                // simply "in transit" (carried) and will be dropped on trap/death.
                if (!this.hasGold && level.get(c, r) === T.GOLD && this.alignedX() && this.alignedY()) {
                    level.set(c, r, T.EMPTY);
                    this.hasGold = true;
                }

                // ---- Falling ----
                const onLadder = level.isLadder(c, r);
                const onBar = level.isBar(c, r);
                const inOpenHole = level.isOpenHole(c, r); // dug pit base holds the guard
                const supportBelow = level.isBlocked(c, r + 1) || level.isLadder(c, r + 1) ||
                                     game.guardActsAsFloor(c, r + 1, this);
                let falling = false;
                if (!onLadder && !onBar && !inOpenHole && !supportBelow) falling = true;

                if (falling) {
                    this.snapX();
                    this.y += GUARD_FALL_SPEED;
                    this.pose = 'fall';
                    const nr = this.row;
                    // land on solid, on a trapped-guard head, or into an open hole base
                    const landed = level.isBlocked(c, nr + 1) || level.isLadder(c, nr + 1) ||
                                   game.guardActsAsFloor(c, nr + 1, this) || level.isOpenHole(c, nr);
                    if (landed && this.alignedY()) this.snapY();
                    if (level.isBlocked(c, this.row + 1) && this.y > this.row * TILE) this.snapY();
                    return;
                }

                // ---- Pursuit AI: classic row-then-column heuristic ----
                this.moveAcc = (this.moveAcc || 0) + dt;
                const speedStep = GUARD_SPEED;
                const target = game.player;
                const tc = target.col, tr = target.row;

                // Decide vertical desire
                let didMove = false;
                const alignedX = this.alignedX();
                const alignedY = this.alignedY();

                // If target is on a different row, try to use ladders.
                const wantUp = tr < r;
                const wantDown = tr > r;

                // Try vertical via ladder first when beneficial and aligned to column
                if (alignedX && onLadder && wantUp && !level.isBlocked(c, r - 1)) {
                    this.snapX();
                    this.y -= speedStep;
                    this.pose = 'climb';
                    didMove = true;
                } else if (alignedX && wantDown && (onLadder || level.isLadder(c, r + 1)) &&
                           !level.isBlocked(c, r + 1)) {
                    this.snapX();
                    this.y += speedStep;
                    this.pose = 'climb';
                    didMove = true;
                }

                if (!didMove) {
                    // Horizontal toward target column, only when on a lane line
                    if (alignedY) {
                        this.snapY();
                        let hdir = 0;
                        if (tc < c) hdir = -1; else if (tc > c) hdir = 1;
                        else hdir = (target.x < this.x) ? -1 : (target.x > this.x ? 1 : 0);

                        if (hdir !== 0) {
                            const nextCol = c + hdir;
                            const blocked = level.isBlocked(nextCol, r);
                            // Don't walk off into a pit unless we want to drop toward the player
                            // below, OR there's an open dug hole ahead (walk in and get trapped).
                            const holeAhead = level.isOpenHole(nextCol, r) ||
                                              level.isOpenHole(nextCol, r + 1);
                            const aheadSupport = level.isBlocked(nextCol, r + 1) ||
                                                 level.isLadder(nextCol, r + 1) ||
                                                 level.isLadder(nextCol, r) ||
                                                 level.isBar(nextCol, r) ||
                                                 game.guardActsAsFloor(nextCol, r + 1, this);
                            const dropOK = wantDown; // allow falling toward a lower player
                            if (!blocked && (aheadSupport || dropOK || onBar || holeAhead)) {
                                this.dir = hdir;
                                this.x += hdir * speedStep;
                                this.pose = onBar ? 'hang' : 'run';
                                didMove = true;
                            } else {
                                // blocked horizontally: try climbing a ladder in this column
                                if (alignedX && onLadder) {
                                    if (wantUp && !level.isBlocked(c, r - 1)) { this.y -= speedStep; didMove = true; this.pose = 'climb'; }
                                    else if (!level.isBlocked(c, r + 1)) { this.y += speedStep; didMove = true; this.pose = 'climb'; }
                                }
                            }
                        }
                    } else {
                        // not aligned to a lane yet: keep settling onto nearest lane
                        this.snapY();
                    }
                }

                if (!didMove) this.pose = onBar ? 'hang' : (onLadder ? 'climb' : 'stand');

                // bounds
                this.x = Math.max(TILE, Math.min(this.x, (COLS - 2) * TILE));
                this.y = Math.max(0, Math.min(this.y, (ROWS - 1) * TILE));

                this.frame += dt;
            }

            draw() {
                if (this.dead) return;
                const x = this.x, y = this.y;
                const d = this.dir < 0 ? 'L' : 'R';
                let name;
                if (this.trapped) name = 'gStand' + d;
                else if (this.pose === 'fall') name = 'gFall' + d;
                else if (this.pose === 'climb') name = (Math.floor(this.frame / 110) % 2) ? 'gClimb2' : 'gClimb1';
                else if (this.pose === 'run') {
                    const f = Math.floor(this.frame / 90) % 3;
                    name = (f === 0 ? 'gStand' : (f === 1 ? 'gRun1' : 'gRun2')) + d;
                } else name = 'gStand' + d;
                drawSprite(name, x, y);

                // carried gold indicator
                if (this.hasGold) {
                    ctx.fillStyle = PAL.y;
                    ctx.fillRect(Math.round(x) + TILE / 2 - 3, Math.round(y) - 4, 6, 5);
                    ctx.fillStyle = PAL.o;
                    ctx.fillRect(Math.round(x) + TILE / 2 - 3, Math.round(y) - 1, 6, 1);
                }
            }
        }

        // -------------------- Tile rendering --------------------
        function drawBrick(x, y, dim) {
            ctx.fillStyle = dim ? '#5a2f10' : '#9a4f1c';
            ctx.fillRect(x, y, TILE, TILE);
            ctx.fillStyle = dim ? '#421f08' : '#7a3b12';
            // mortar lines (offset brick rows)
            ctx.fillRect(x, y + TILE / 2, TILE, 2);
            ctx.fillRect(x + TILE / 2, y, 2, TILE / 2);
            ctx.fillRect(x, y + TILE / 2, 2, TILE / 2);
            ctx.fillRect(x + TILE - 2, y, 2, TILE / 2);
            // highlight
            ctx.fillStyle = dim ? '#6e3a14' : '#b86a2e';
            ctx.fillRect(x + 2, y + 2, TILE / 2 - 3, 3);
            ctx.fillRect(x + TILE / 2 + 1, y + TILE / 2 + 2, TILE / 2 - 3, 3);
        }

        function drawSolid(x, y) {
            ctx.fillStyle = '#3a3f4a';
            ctx.fillRect(x, y, TILE, TILE);
            ctx.fillStyle = '#52596a';
            ctx.fillRect(x + 1, y + 1, TILE - 2, TILE - 2);
            ctx.fillStyle = '#2a2e36';
            ctx.fillRect(x + 3, y + 3, TILE - 6, TILE - 6);
            ctx.fillStyle = '#6a7286';
            ctx.fillRect(x + 3, y + 3, TILE - 6, 2);
        }

        function drawLadder(x, y, color) {
            ctx.fillStyle = color;
            ctx.fillRect(x + 4, y, 3, TILE);
            ctx.fillRect(x + TILE - 7, y, 3, TILE);
            for (let i = 3; i < TILE; i += 7) {
                ctx.fillRect(x + 4, y + i, TILE - 8, 2);
            }
        }

        function drawBar(x, y) {
            ctx.fillStyle = '#caa23a';
            ctx.fillRect(x, y + 3, TILE, 3);
            ctx.fillStyle = '#8a6a18';
            ctx.fillRect(x, y + 6, TILE, 1);
        }

        function drawGold(x, y, t) {
            // chest/bar style with sparkle
            ctx.fillStyle = '#a06a00';
            ctx.fillRect(x + 4, y + 9, TILE - 8, TILE - 13);
            ctx.fillStyle = '#ffd700';
            ctx.fillRect(x + 5, y + 10, TILE - 10, TILE - 16);
            ctx.fillStyle = '#fff2a0';
            ctx.fillRect(x + 6, y + 11, TILE - 12, 2);
            // lid line
            ctx.fillStyle = '#a06a00';
            ctx.fillRect(x + 4, y + 12, TILE - 8, 1);
            // sparkle
            const tw = prefersReducedMotion ? 1 : (Math.floor(t / 250) % 2);
            if (tw) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x + TILE - 9, y + 5, 2, 2);
                ctx.fillRect(x + TILE - 10, y + 4, 1, 1);
                ctx.fillRect(x + TILE - 8, y + 4, 1, 1);
                ctx.fillRect(x + TILE - 9, y + 6, 1, 1);
            }
        }

        // -------------------- Game controller --------------------
        class Game {
            constructor() {
                this.state = 'start';   // start|playing|levelComplete|gameover
                this.level = null;
                this.player = null;
                this.guards = [];
                this.currentLevel = 0;
                this.score = 0;
                this.levelStartScore = 0;
                this.simAccumulator = 0;
                this.lives = 3;
                this.bannerTimer = 0;
                this.bannerText = '';
                this.stepAcc = 0;
                this.lastTime = 0;
                // Read is guarded: localStorage access itself throws a
                // SecurityError when storage is blocked (Safari Private mode,
                // sandboxed iframe). An unguarded read here would abort the
                // whole bootstrap and leave every button (incl. Back to Site)
                // dead — so fall back to no persistence instead.
                let storedHigh = 0;
                try {
                    storedHigh = parseInt(localStorage.getItem('loderunner_high') || '0', 10) || 0;
                } catch (e) { /* storage blocked — play without a saved high score */ }
                this.high = storedHigh;
                this.keys = { left: false, right: false, up: false, down: false };
                this.setupKeyboard();
            }

            // ---- input ----
            setupKeyboard() {
                const down = (e) => {
                    const k = e.key.toLowerCase();
                    let handled = true;
                    switch (k) {
                        case 'arrowleft': case 'a': this.keys.left = true; break;
                        case 'arrowright': case 'd': this.keys.right = true; break;
                        case 'arrowup': case 'w': this.keys.up = true; break;
                        case 'arrowdown': case 's': this.keys.down = true; break;
                        case 'z': case 'q': this.digLeft(); break;
                        case 'x': case 'e': this.digRight(); break;
                        case 'r': if (!e.repeat) this.restartLevel(); break;
                        default: handled = false;
                    }
                    if (handled) { e.preventDefault(); Sound.resume(); }
                };
                const up = (e) => {
                    switch (e.key.toLowerCase()) {
                        case 'arrowleft': case 'a': this.keys.left = false; break;
                        case 'arrowright': case 'd': this.keys.right = false; break;
                        case 'arrowup': case 'w': this.keys.up = false; break;
                        case 'arrowdown': case 's': this.keys.down = false; break;
                    }
                };
                document.addEventListener('keydown', down);
                document.addEventListener('keyup', up);
            }

            // ---- lifecycle ----
            start() {
                const alreadyRunning = this.state === 'playing';
                Sound.resume();
                document.getElementById('startScreen').classList.add('hidden');
                this.currentLevel = 0;
                this.score = 0;
                this.levelStartScore = 0;
                this.simAccumulator = 0;
                this.lives = 3;
                this.loadLevel();
                this.state = 'playing';
                this.updateMobileControlsVisibility();
                this.lastTime = performance.now();
                if (!alreadyRunning) requestAnimationFrame((t) => this.loop(t));
            }

            loadLevel() {
                this.score = this.levelStartScore;
                this.simAccumulator = 0;
                const map = LEVELS[this.currentLevel % LEVELS.length];
                this.level = new Level(map, this.currentLevel + 1);
                this.player = new Player(this.level, this.level.playerStart.col, this.level.playerStart.row);
                this.guards = this.level.guardStarts.map((g, i) =>
                    new Guard(this.level, g.col, g.row, i));
                this.keys.left = this.keys.right = this.keys.up = this.keys.down = false;
                this.bannerText = 'LEVEL ' + (this.currentLevel + 1);
                this.bannerTimer = BANNER_MS;
                this.updateHUD();
            }

            restartLevel() {
                // Neutral "try this level again" — reloads the current level
                // WITHOUT costing a life, matching the "R  Restart Level" help.
                if (this.state === 'playing' && !arcade.paused) { this.loadLevel(); this.showMessage('Level reset. This attempt’s points reset too.'); }
            }

            loseLife() {
                if (this.state !== 'playing') return;
                this.lives--;
                Sound.death();
                this.updateHUD();
                if (this.lives <= 0) {
                    this.gameOver();
                } else {
                    this.loadLevel();
                    this.showMessage('Caught! Try again...');
                }
            }

            nextLevel() {
                document.getElementById('levelComplete').classList.add('hidden');
                this.currentLevel++;
                this.score += 1500; // level clear bonus
                this.levelStartScore = this.score;
                if (this.currentLevel >= LEVELS.length) {
                    this.showMessage('All levels cleared! Looping with tougher play...');
                }
                this.loadLevel();
                this.state = 'playing';
                this.updateMobileControlsVisibility();
                this.lastTime = performance.now();
                requestAnimationFrame((t) => this.loop(t));
            }

            completeLevel() {
                this.state = 'levelComplete';
                this.score += 200 + this.lives * 100;
                this.recordHigh();
                Sound.levelDone();
                document.getElementById('levelScore').textContent = this.score;
                document.getElementById('levelComplete').classList.remove('hidden');
                this.updateMobileControlsVisibility();
            }

            gameOver() {
                this.state = 'gameover';
                this.recordHigh();
                Sound.gameOver();
                document.getElementById('finalScore').textContent = this.score;
                document.getElementById('highScore').textContent = this.high;
                document.getElementById('gameOver').classList.remove('hidden');
                this.updateMobileControlsVisibility();
            }

            restart() {
                document.getElementById('gameOver').classList.add('hidden');
                this.start();
            }

            recordHigh() {
                if (this.score > this.high) {
                    this.high = this.score;
                    try { localStorage.setItem('loderunner_high', String(this.high)); } catch (e) {}
                }
            }

            // ---- digging ----
            digLeft() { this.digAt(-1); }
            digRight() { this.digAt(1); }
            digAt(side) {
                if (arcade.paused || this.state !== 'playing' || !this.player || this.player.falling) return;
                const p = this.player;
                // Require vertical lane alignment so the target floor row is unambiguous.
                if (!p.alignedY()) return;
                const c = p.col + side;     // brick diagonally below-left / below-right
                const r = p.row + 1;
                if (this.level.dig(c, r)) {
                    p.dir = side;
                    Sound.dig();
                }
            }

            // ---- floor helpers (trapped guards / heads act as floor) ----
            guardActsAsFloor(c, r, ignore) {
                for (let i = 0; i < this.guards.length; i++) {
                    const g = this.guards[i];
                    if (g === ignore || g.dead) continue;
                    if (g.trapped && g.col === c && g.row === r) return true;
                }
                return false;
            }

            // a trapped guard's head allows the player to run across (treated as floor)
            afterPlayerMove() {
                // gold pickup at player cell
                const p = this.player;
                if (this.level.collectGold(p.col, p.row)) {
                    this.score += 75;
                    Sound.gold();
                    this.checkGoldComplete();
                    this.updateHUD();
                }
                // exit reached?
                if (this.level.exitRevealed && this.level.get(p.col, p.row) === T.EXIT &&
                    p.row <= 1) {
                    this.completeLevel();
                }
            }

            checkGoldComplete() {
                if (this.level.goldCollected >= this.level.goldTotal) {
                    if (this.level.revealExitIfDone()) {
                        this.showMessage('Exit ladder revealed! Climb to the top!');
                    }
                }
            }

            onGuardTrapped(g) {
                this.score += 75;
                Sound.trap();
                this.updateHUD();
            }

            onHoleRefill(hole) {
                // any guard occupying this hole dies; player occupying dies
                for (let i = 0; i < this.guards.length; i++) {
                    const g = this.guards[i];
                    if (g.dead) continue;
                    if (g.col === hole.c && g.row === hole.r) {
                        if (g.hasGold) {
                            g.hasGold = false;
                            this.level.dropGold(hole.c, hole.r - 1 >= 1 ? hole.r - 1 : hole.r);
                        }
                        g.dead = true;
                        g.respawnTimer = GUARD_RESPAWN_MS;
                        this.score += 250;
                        Sound.guardDie();
                        this.updateHUD();
                    }
                }
                const p = this.player;
                if (p && !p.dead && p.col === hole.c && p.row === hole.r) {
                    this.loseLife();
                }
            }

            findRespawnSpot() {
                // prefer an empty top-row cell away from the player
                const candidates = [];
                for (let c = 1; c < COLS - 1; c++) {
                    if (this.level.get(c, 1) === T.EMPTY) candidates.push({ col: c, row: 1 });
                }
                if (candidates.length === 0) {
                    for (let c = 1; c < COLS - 1; c++) {
                        if (this.level.get(c, 2) === T.EMPTY) candidates.push({ col: c, row: 2 });
                    }
                }
                if (candidates.length === 0) return { col: 2, row: 1 };
                // farthest from player
                let best = candidates[0], bestD = -1;
                for (const cd of candidates) {
                    const d = Math.abs(cd.col - this.player.col);
                    if (d > bestD) { bestD = d; best = cd; }
                }
                return best;
            }

            // subtle footstep throttle
            stepTick() {
                this.stepAcc++;
                if (this.stepAcc % 12 === 0) return true;
                return false;
            }

            // ---- main update ----
            update(dt) {
                if (this.state !== 'playing') return;
                if (this.bannerTimer > 0) this.bannerTimer -= dt;

                this.level.update(dt, this);
                this.player.update(dt, this.keys, this);

                for (let i = 0; i < this.guards.length; i++) {
                    this.guards[i].update(dt, this);
                }

                // collisions with guards (non-trapped) => lose life
                if (!this.player.dead) {
                    for (let i = 0; i < this.guards.length; i++) {
                        const g = this.guards[i];
                        if (g.dead || g.trapped) continue;
                        const dx = Math.abs(g.x - this.player.x);
                        const dy = Math.abs(g.y - this.player.y);
                        if (dx < TILE * 0.6 && dy < TILE * 0.6) {
                            this.loseLife();
                            break;
                        }
                    }
                }
                this.updateHUD();
            }

            // ---- HUD ----
            updateHUD() {
                this.recordHigh();
                if (this.level) arcade.status(
                    this.level.exitRevealed ? `Exit open! Climb the green ladder · Best ${this.high.toLocaleString()}` : `Gold ${this.level.goldCollected}/${this.level.goldTotal} · Best ${this.high.toLocaleString()} · R retries this level`,
                    this.level.goldCollected / Math.max(1, this.level.goldTotal) * 100);
                document.getElementById('levelDisplay').textContent = this.level ? this.level.levelNumber : 1;
                document.getElementById('scoreDisplay').textContent = this.score;
                document.getElementById('livesDisplay').textContent = Math.max(0, this.lives);
                if (this.level) {
                    // goldTotal is fixed for the level; collected counts what the player banked.
                    document.getElementById('goldDisplay').textContent =
                        this.level.goldCollected + '/' + this.level.goldTotal;
                }
            }

            // ---- render ----
            draw() {
                ctx.fillStyle = '#05050a';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                const lvl = this.level;
                const now = performance.now();

                // tiles
                for (let r = 0; r < ROWS; r++) {
                    for (let c = 0; c < COLS; c++) {
                        const t = lvl.grid[r][c];
                        const x = c * TILE, y = r * TILE;
                        switch (t) {
                            case T.BRICK: {
                                const h = lvl.holeAt(c, r);
                                drawBrick(x, y, h && lvl.holeRefilling(h) && !prefersReducedMotion &&
                                    (Math.floor(now / 120) % 2));
                                break;
                            }
                            case T.SOLID: drawSolid(x, y); break;
                            case T.LADDER: drawLadder(x, y, '#d9952f'); break;
                            case T.BAR: drawBar(x, y); break;
                            case T.GOLD: drawGold(x, y, now); break;
                            case T.EXIT: {
                                let col;
                                if (prefersReducedMotion) col = '#36ff5a';
                                else col = (Math.floor(now / 180) % 2) ? '#36ff5a' : '#0a9a2a';
                                drawLadder(x, y, col);
                                break;
                            }
                            default: break;
                        }
                    }
                }

                // crumble animation overlay for freshly dug holes
                for (let i = 0; i < lvl.holes.length; i++) {
                    const h = lvl.holes[i];
                    if (h.state === 'crumble') {
                        const x = h.c * TILE, y = h.r * TILE;
                        const p = h.t / DIG_CRUMBLE_MS;
                        ctx.fillStyle = '#7a3b12';
                        const k = Math.floor((1 - p) * 6);
                        for (let n = 0; n < k; n++) {
                            const px = x + (n * 5 + (n % 2) * 3) % (TILE - 3);
                            const py = y + ((n * 7) % (TILE - 3));
                            ctx.fillRect(px, py, 3, 3);
                        }
                    }
                }

                // Visible refill timers make each hole a readable tactical choice.
                for (const hole of lvl.holes) {
                    if (hole.state !== 'open') continue;
                    const remaining = Math.max(0, 1 - hole.open / DIG_REFILL_MS);
                    ctx.fillStyle = '#fff';
                    ctx.fillRect(hole.c * TILE + 2, hole.r * TILE + TILE - 4, TILE - 4, 2);
                    ctx.fillStyle = remaining < 0.25 ? '#ff6262' : '#36ffce';
                    ctx.fillRect(hole.c * TILE + 2, hole.r * TILE + TILE - 4, (TILE - 4) * remaining, 2);
                }
                // entities
                for (let i = 0; i < this.guards.length; i++) this.guards[i].draw();
                this.player.draw();

                // optional subtle scanlines (skip under reduced motion for calm)
                if (!prefersReducedMotion) {
                    ctx.fillStyle = 'rgba(0,0,0,0.10)';
                    for (let y = 0; y < canvas.height; y += 3) ctx.fillRect(0, y, canvas.width, 1);
                }

                // LEVEL banner
                if (this.bannerTimer > 0) {
                    ctx.fillStyle = 'rgba(0,0,0,0.7)';
                    ctx.fillRect(0, canvas.height / 2 - 28, canvas.width, 56);
                    pixelText(this.bannerText, canvas.width / 2, canvas.height / 2 - 7, 4, '#36ff5a', true);
                }
            }

            loop(t) {
                let dt = t - this.lastTime;
                this.lastTime = t;
                if (dt > 50) dt = 50; // clamp big gaps (tab switches)
                if (this.state === 'playing') {
                    if (!arcade.paused) {
                        this.simAccumulator += dt;
                        while (this.simAccumulator >= 1000 / 60 && this.state === 'playing') {
                            this.simAccumulator -= 1000 / 60;
                            this.update(1000 / 60);
                        }
                        this.draw();
                    } else this.simAccumulator = 0;
                    requestAnimationFrame((tt) => this.loop(tt));
                } else if (this.state === 'levelComplete' || this.state === 'gameover') {
                    // freeze-frame final render already drawn; stop looping
                }
            }

            showMessage(text) {
                const existing = document.getElementById('message');
                if (existing) existing.remove();
                const msg = document.createElement('div');
                msg.id = 'message';
                msg.textContent = text;
                document.body.appendChild(msg);
                setTimeout(() => { if (msg.parentNode) msg.remove(); }, 1800);
            }

            updateMobileControlsVisibility() {
                const mobileControls = document.getElementById('mobileControls');
                const startScreen = document.getElementById('startScreen');
                const gameOver = document.getElementById('gameOver');
                const levelComplete = document.getElementById('levelComplete');
                const dialogVisible = !startScreen.classList.contains('hidden') ||
                    !gameOver.classList.contains('hidden') ||
                    !levelComplete.classList.contains('hidden');
                if (dialogVisible) mobileControls.classList.add('hidden');
                else mobileControls.classList.remove('hidden');
            }
        }

        // -------------------- Tiny pixel font (for in-canvas text) --------------------
        // 3x5 uppercase glyphs, drawn with ctx rects (no web fonts).
        const FONT = {
            'A': ["111", "101", "111", "101", "101"],
            'B': ["110", "101", "110", "101", "110"],
            'C': ["111", "100", "100", "100", "111"],
            'D': ["110", "101", "101", "101", "110"],
            'E': ["111", "100", "110", "100", "111"],
            'F': ["111", "100", "110", "100", "100"],
            'G': ["111", "100", "101", "101", "111"],
            'H': ["101", "101", "111", "101", "101"],
            'I': ["111", "010", "010", "010", "111"],
            'J': ["001", "001", "001", "101", "111"],
            'K': ["101", "110", "100", "110", "101"],
            'L': ["100", "100", "100", "100", "111"],
            'M': ["101", "111", "111", "101", "101"],
            'N': ["101", "111", "111", "111", "101"],
            'O': ["111", "101", "101", "101", "111"],
            'P': ["111", "101", "111", "100", "100"],
            'Q': ["111", "101", "101", "110", "011"],
            'R': ["111", "101", "111", "110", "101"],
            'S': ["111", "100", "111", "001", "111"],
            'T': ["111", "010", "010", "010", "010"],
            'U': ["101", "101", "101", "101", "111"],
            'V': ["101", "101", "101", "101", "010"],
            'W': ["101", "101", "111", "111", "101"],
            'X': ["101", "101", "010", "101", "101"],
            'Y': ["101", "101", "010", "010", "010"],
            'Z': ["111", "001", "010", "100", "111"],
            '0': ["111", "101", "101", "101", "111"],
            '1': ["010", "110", "010", "010", "111"],
            '2': ["111", "001", "111", "100", "111"],
            '3': ["111", "001", "111", "001", "111"],
            '4': ["101", "101", "111", "001", "001"],
            '5': ["111", "100", "111", "001", "111"],
            '6': ["111", "100", "111", "101", "111"],
            '7': ["111", "001", "010", "010", "010"],
            '8': ["111", "101", "111", "101", "111"],
            '9': ["111", "101", "111", "001", "111"],
            ' ': ["000", "000", "000", "000", "000"],
            '-': ["000", "000", "111", "000", "000"],
            '!': ["010", "010", "010", "000", "010"],
            '/': ["001", "001", "010", "100", "100"],
            ':': ["000", "010", "000", "010", "000"]
        };

        function pixelText(text, cx, cy, scale, color, center) {
            text = String(text).toUpperCase();
            const gw = 3, gh = 5, gap = 1;
            const charW = (gw + gap) * scale;
            const totalW = text.length * charW - gap * scale;
            let x = center ? Math.round(cx - totalW / 2) : Math.round(cx);
            const y = center ? Math.round(cy - (gh * scale) / 2) : Math.round(cy);
            ctx.fillStyle = color;
            for (let i = 0; i < text.length; i++) {
                const g = FONT[text[i]] || FONT[' '];
                for (let row = 0; row < gh; row++) {
                    const line = g[row];
                    for (let col = 0; col < gw; col++) {
                        if (line[col] === '1') {
                            ctx.fillRect(x + col * scale, y + row * scale, scale, scale);
                        }
                    }
                }
                x += charW;
            }
        }

        // -------------------- Bootstrap --------------------
        const game = new Game();
        game.updateMobileControlsVisibility();

        // Draw the start-screen background once so the canvas isn't blank behind the dialog.
        (function drawStartBackdrop() {
            ctx.fillStyle = '#05050a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            pixelText('LODE RUNNER', canvas.width / 2, canvas.height / 2 - 40, 6, '#36ff5a', true);
            pixelText('PRESS START', canvas.width / 2, canvas.height / 2 + 30, 3, '#00ffff', true);
        })();

        // Back / dialog button wiring
        const goBack = () => { window.location.href = '../index.html'; };
        document.getElementById('mainBackBtn')?.addEventListener('click', goBack);
        document.getElementById('startBackBtn').addEventListener('click', goBack);
        document.getElementById('gameOverBackBtn').addEventListener('click', goBack);
        document.getElementById('startBtn').addEventListener('click', () => game.start());
        document.getElementById('tryAgainBtn').addEventListener('click', () => game.restart());
        document.getElementById('nextLevelBtn').addEventListener('click', () => game.nextLevel());

        arcade.mount({
          title: 'Lode Runner',
          help: 'Move and climb with arrows or WASD. Z/Q digs left; X/E digs right. Collect every gold pile, then climb the revealed green exit. The bars in holes show time until refill. R retries the level and resets its points. P pauses; M toggles sound.',
          active: () => game.state === 'playing',
          clearInput: () => Object.keys(game.keys).forEach(key => { game.keys[key] = false; }),
          restart: () => { game.start(); },
          retryLevel: () => game.restartLevel(),
          onResume: () => { game.lastTime = performance.now(); game.simAccumulator = 0; },
        });
        arcade.status('Collect the gold. Outsmart the guards. Find the exit.');
        // -------------------- Mobile touch controls --------------------
        const btnUp = document.getElementById('btnUp');
        const btnDown = document.getElementById('btnDown');
        const btnLeft = document.getElementById('btnLeft');
        const btnRight = document.getElementById('btnRight');
        const btnDigLeft = document.getElementById('btnDigLeft');
        const btnDigRight = document.getElementById('btnDigRight');

        function setupDpadButton(button, keyName) {
            const activate = (e) => {
                e.preventDefault();
                Sound.resume();
                game.keys[keyName] = true;
                button.classList.add('active');
            };
            const deactivate = (e) => {
                if (e) e.preventDefault();
                game.keys[keyName] = false;
                button.classList.remove('active');
            };
            bindPointer(button, () => activate({ preventDefault() {} }), () => deactivate({ preventDefault() {} }));
        }

        function setupDigButton(button, digMethod) {
            const activate = (e) => {
                e.preventDefault();
                Sound.resume();
                button.classList.add('active');
                digMethod.call(game);
            };
            const deactivate = (e) => {
                if (e) e.preventDefault();
                button.classList.remove('active');
            };
            bindPointer(button, () => activate({ preventDefault() {} }), () => deactivate({ preventDefault() {} }));
        }

        setupDpadButton(btnUp, 'up');
        setupDpadButton(btnDown, 'down');
        setupDpadButton(btnLeft, 'left');
        setupDpadButton(btnRight, 'right');
        setupDigButton(btnDigLeft, game.digLeft);
        setupDigButton(btnDigRight, game.digRight);

        document.getElementById('gameContainer').addEventListener('touchmove', (e) => {
            if (game.state === 'playing') e.preventDefault();
        }, { passive: false });
    })();
